// Locale-aware formatting for the dashboard. Uses Intl with the UI language,
// so numbers, dates, weekdays and relative times need no translation keys.

const numberFormats = new Map();
function numberFormat(language, options = {}) {
  const key = `${language}|${JSON.stringify(options)}`;
  if (!numberFormats.has(key)) {
    try {
      numberFormats.set(key, new Intl.NumberFormat(language, options));
    } catch {
      numberFormats.set(key, new Intl.NumberFormat("en", options));
    }
  }
  return numberFormats.get(key);
}

export function formatNumber(value, language, options) {
  if (!Number.isFinite(value)) return "–";
  return numberFormat(language, options).format(value);
}

// 0.54 -> "54%"
export function formatPercent(ratio, language, maximumFractionDigits = 0) {
  if (!Number.isFinite(ratio)) return "–";
  return numberFormat(language, {
    style: "percent",
    maximumFractionDigits,
  }).format(ratio);
}

// "2026-09-22" is a calendar day in the store's zone. Parsing it with
// new Date("2026-09-22") would read it as UTC midnight and show the day
// before anywhere west of Greenwich, so build it from its parts instead.
export function parseDay(day) {
  const [y, m, d] = String(day).split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, d || 1);
}

export function formatDay(day, language, options = { month: "short", day: "numeric" }) {
  const date = parseDay(day);
  if (!date) return "";
  return safeDateFormat(language, options).format(date);
}

// "2026-05" -> "May 2026"
export function formatMonth(month, language) {
  return formatDay(`${month}-01`, language, { month: "short", year: "numeric" });
}

// Birthdays come as "MM-DD" with no year.
export function formatBirthday(monthDay, language) {
  const [m, d] = String(monthDay).split("-").map(Number);
  if (!m || !d) return "";
  return safeDateFormat(language, { month: "short", day: "numeric" }).format(
    new Date(2000, m - 1, d),
  );
}

export function formatLongToday(language, now = new Date()) {
  return safeDateFormat(language, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);
}

// dow: 0 = Sunday, as the insights heatmap sends it.
export function weekdayName(dow, language, style = "short") {
  // 1 Jan 2023 was a Sunday.
  return safeDateFormat(language, { weekday: style, timeZone: "UTC" }).format(
    new Date(Date.UTC(2023, 0, 1 + dow)),
  );
}

export function formatRelative(iso, language, now = new Date()) {
  const time = new Date(iso);
  if (Number.isNaN(time.getTime())) return "";
  const seconds = Math.round((time.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  let rtf;
  try {
    rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  } catch {
    rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  }
  if (abs < 60) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), "hour");
  if (abs < 86400 * 7) return rtf.format(Math.round(seconds / 86400), "day");
  // Past this year the year is shown, or an old date reads as a recent one.
  const sameYear = time.getFullYear() === now.getFullYear();
  return safeDateFormat(
    language,
    sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" },
  ).format(time);
}

function safeDateFormat(language, options) {
  try {
    return new Intl.DateTimeFormat(language, options);
  } catch {
    return new Intl.DateTimeFormat("en", options);
  }
}
